import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, ArrowRight, Users, Building2, Clock, CheckSquare } from "lucide-react";
import { useGaContext } from "@/hooks/useGaContext";

const SETUP_STEP_KEYS = ["license_assigned", "first_login", "first_employer", "first_census", "first_enrollment"];

function SetupProgressCell({ gaId, brokerAccountId }: { gaId: string; brokerAccountId: string }) {
  const { data: progress } = useQuery<Record<string, boolean>>({
    queryKey: ["/api/ga", gaId, "broker-links", brokerAccountId, "setup-progress"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links/${brokerAccountId}/setup-progress`);
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!gaId && !!brokerAccountId,
  });

  if (!progress) return <span className="text-muted-foreground text-xs">—</span>;
  const completed = SETUP_STEP_KEYS.filter((k) => progress[k]).length;
  const total = SETUP_STEP_KEYS.length;
  const color = completed === total ? "text-green-600 dark:text-green-400" : completed >= 3 ? "text-primary" : "text-amber-500 dark:text-amber-400";

  return (
    <span className={`text-xs font-semibold ${color} flex items-center gap-1`}>
      <CheckSquare className="w-3.5 h-3.5" />
      {completed}/{total}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string }> = {
    active: { label: "Active", className: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300" },
    pending: { label: "Invited", className: "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300" },
    license_revoked: { label: "Revoked", className: "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300" },
  };
  const cfg = map[status] || { label: status, className: "bg-muted text-muted-foreground" };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function AdoptionBadge({ score, band }: { score: number; band: string }) {
  const color =
    band === "power_user" ? "text-green-600 dark:text-green-400" :
    band === "active" ? "text-primary" : "text-muted-foreground";
  return (
    <span className={`font-bold text-sm ${color}`}>{score}</span>
  );
}

function BuildAccessCell({ buildAccess }: { buildAccess: any }) {
  if (!buildAccess) return <span className="text-muted-foreground text-xs">Not granted</span>;
  if (buildAccess.status === "active") {
    return (
      <span className="text-green-600 dark:text-green-400 text-xs font-medium">
        Active{buildAccess.expiresAt ? ` · expires ${new Date(buildAccess.expiresAt).toLocaleDateString()}` : ""}
      </span>
    );
  }
  if (buildAccess.status === "expired") return <span className="text-amber-600 dark:text-amber-400 text-xs">Expired</span>;
  return <span className="text-muted-foreground text-xs">Revoked</span>;
}

export default function GABrokerAccounts() {
  const { gaId } = useGaContext();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBand, setFilterBand] = useState("all");

  const { data: brokers = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "broker-links"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/broker-links`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const filtered = brokers.filter((b) => {
    const matchSearch =
      !search ||
      (b.brokerAgencyName || "").toLowerCase().includes(search.toLowerCase()) ||
      (b.brokerNpn || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || b.status === filterStatus;
    const matchBand = filterBand === "all" || (b.snapshot?.adoptionBand === filterBand);
    return matchSearch && matchStatus && matchBand;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Broker Accounts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{brokers.length} broker{brokers.length !== 1 ? "s" : ""} in your network</p>
        </div>
        <Link href="/ga/license-pool">
          <Button size="sm">
            Assign License
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by agency name or NPN..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Invited</SelectItem>
            <SelectItem value="license_revoked">Revoked</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterBand} onValueChange={setFilterBand}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Adoption Band" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Bands</SelectItem>
            <SelectItem value="building">Building</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="power_user">Power User</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No broker accounts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted border-b border-border">
                  <tr className="text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Agency Name</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">Setup</th>
                    <th className="text-left px-4 py-3">Employers</th>
                    <th className="text-left px-4 py-3">Last Login</th>
                    <th className="text-left px-4 py-3">Build Access</th>
                    <th className="text-left px-4 py-3">Funding</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((b: any) => (
                    <tr key={b.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-3">
                        <Link href={`/ga/brokers/${b.brokerAccountId}`}>
                          <span className="font-medium text-primary hover:underline cursor-pointer">
                            {b.brokerAgencyName || "—"}
                          </span>
                        </Link>
                        {b.brokerNpn && (
                          <p className="text-xs text-muted-foreground mt-0.5">NPN: {b.brokerNpn}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={b.status} />
                      </td>
                      <td className="px-4 py-3">
                        {b.snapshot ? (
                          <AdoptionBadge score={b.snapshot.adoptionScore || 0} band={b.snapshot.adoptionBand || "building"} />
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <SetupProgressCell brokerAccountId={b.brokerAccountId} gaId={gaId} />
                      </td>
                      <td className="px-4 py-3 text-foreground">{b.snapshot?.employersTotal ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {b.snapshot?.lastLoginAt
                          ? new Date(b.snapshot.lastLoginAt).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-4 py-3">
                        <BuildAccessCell buildAccess={b.buildAccess} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${b.gaFunded ? "text-primary" : "text-muted-foreground"}`}>
                          {b.gaFunded ? "GA-funded" : "Self-funded"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link href={`/ga/brokers/${b.brokerAccountId}`}>
                          <Button variant="ghost" size="sm" className="h-7 text-xs">
                            View <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
